export type Faq = {
    question: string;
    answer: string[];
};

export type FaqSection = {
    id: string;
    title: string;
    faqs: Faq[];
};

export const SUPPORT_EMAIL = 'hello@wave.me';

export const FAQ_SECTIONS: FaqSection[] = [
    {
        id: 'about-wave',
        title: 'About Wave',
        faqs: [
            {
                question: 'What is Wave?',
                answer: [
                    'Wave is a platform that helps social media creators make money from the kind of content they already create.',
                    'Right now, most Wave campaigns are focused on music and sound promotion. Clients give us a song and a brief, and we match those campaigns with creators whose content fits — from edits and clips to fandom, sports, aesthetic and other niche content.',
                    'Wave currently supports TikTok, Instagram Reels and YouTube Shorts.',
                ],
            },
            {
                question: 'How did you find my account?',
                answer: [
                    'Our team is constantly looking for great creator accounts that could suit upcoming campaigns. We find creators through things like relevant hashtags, content searches and manual research.',
                    "If we've invited you to Wave, it's because we think your page could be a good fit for the kinds of briefs we receive.",
                ],
            },
            {
                question: 'Is Wave legitimate?',
                answer: [
                    'Yes. Wave works with thousands of creators and currently pays out more than $100,000 to creators every month.',
                    'You never need to give us your social media password or login details.',
                ],
            },
            {
                question: 'Can anyone join Wave?',
                answer: [
                    'Wave is currently invite-led.',
                    "If you've received an invite, you can apply to join. You're also welcome to share the signup link with other creators you think would be a good fit.",
                    'Applications are normally reviewed within 2–3 days.',
                ],
            },
            {
                question: 'What do you look for when approving creators?',
                answer: [
                    'We generally look for public, active accounts that post regularly, produce good-quality content and naturally use music within their content.',
                    "Accounts containing racist, offensive, overtly sexual or otherwise inappropriate content won't be accepted.",
                    'There is no set follower minimum.',
                ],
            },
        ],
    },
    {
        id: 'campaigns',
        title: 'Campaigns',
        faqs: [
            {
                question: 'How do I get campaigns?',
                answer: [
                    "When a campaign matches your page, we'll send an invitation to your email.",
                    "Campaign matching can depend on your content niche, location, language, platform, rates, campaign preferences, past performance and the client's specific brief. Wave is designed to build different creator pools around exactly these kinds of criteria.",
                    'There is no guaranteed number of campaigns. It depends on the briefs we have live and how well they match your page.',
                ],
            },
            {
                question: 'Where can I see my campaigns?',
                answer: [
                    'New campaign invitations are currently sent by email.',
                    "You can also log into Wave to see campaigns you've been invited to, accepted, declined or completed.",
                ],
            },
            {
                question: 'What should I check before joining a campaign?',
                answer: [
                    'Always read the campaign page carefully before opting in.',
                    'Pay particular attention to the brief, the sound you need to use, the campaign deadline, and the payment terms, including any CPM thresholds or earning limits.',
                ],
            },
            {
                question: "What does “I'm in” mean?",
                answer: [
                    "Selecting “I'm in” reserves your place on the campaign for a set amount of time.",
                    'Campaigns are often oversubscribed, so receiving an invitation alone does not guarantee your spot.',
                    'Always opt in if you genuinely intend to take part.',
                ],
            },
            {
                question: "What happens if I don't want to take part?",
                answer: [
                    'Select “Not this time.”',
                    "Declining a campaign won't automatically count against you. However, consistently ignoring every invitation can make your account appear inactive and may mean you receive fewer invitations in future.",
                ],
            },
            {
                question: "What if I opt in but don't post in time?",
                answer: [
                    "If your reservation expires and you haven't submitted any posts, you can usually return to the original invite and opt in again, provided the campaign is still open.",
                    'Repeatedly reserving campaign spaces and not delivering may result in your account being flagged and receiving fewer future campaign invitations.',
                ],
            },
            {
                question: 'Can I opt out after opting in?',
                answer: ["Yes, provided you haven't submitted a post yet."],
            },
        ],
    },
    {
        id: 'payment-types',
        title: 'Payment types',
        faqs: [
            {
                question: 'What types of campaigns does Wave run?',
                answer: [
                    'Wave mainly uses three payment models.',
                    "Fixed fee: you're paid an agreed amount for completing the required post or posts.",
                    "CPM: you're paid based on eligible views. For example, at a $2 CPM, 10,000 eligible views would earn $20. Campaigns can include earning caps or minimum performance thresholds.",
                    'Bundles: several posts are commissioned together for an agreed package price.',
                    'Fixed fee and CPM are core campaign models used by Wave.',
                    'Most Wave campaigns currently use CPM.',
                ],
            },
            {
                question: 'How do CPM campaigns work?',
                answer: [
                    'CPM stands for cost per mille, meaning payment per 1,000 eligible views.',
                    'Each campaign may have a maximum amount you can earn per post, an overall maximum you can earn, and sometimes a minimum view or engagement threshold.',
                    'Always check the campaign invitation before opting in.',
                ],
            },
            {
                question: 'If a campaign has a minimum view threshold, do the views below it still count?',
                answer: [
                    'Yes.',
                    'For example, if a campaign requires at least 2,000 views before you become eligible and your post reaches 5,000 views, all 5,000 views count toward your earnings.',
                ],
            },
            {
                question: 'Why should I post early on CPM campaigns?',
                answer: [
                    'The overall campaign budget is shared between participating creators.',
                    'Opting in reserves your place, but it does not reserve a specific portion of the CPM budget for you.',
                    'That means posting earlier gives your content more time to earn before the campaign budget is used up.',
                ],
            },
            {
                question: 'Is the maximum CPM earning amount guaranteed?',
                answer: [
                    'No.',
                    'The amount shown is the maximum you can earn, not a guaranteed payout. Your actual earnings depend on your eligible views and how much campaign budget remains.',
                ],
            },
            {
                question: 'How do fixed-fee campaigns work?',
                answer: [
                    'For fixed-fee campaigns, you agree to create the required post or posts for a set amount.',
                    'When you opt in, the relevant budget is reserved for you.',
                ],
            },
            {
                question: 'How do bundles work?',
                answer: [
                    'Bundles are an agreed package covering multiple posts.',
                    'Your bundle payment moves into Pending once the required content has been delivered. If you complete only part of the bundle, payment is calculated pro-rata for the posts completed.',
                ],
            },
        ],
    },
    {
        id: 'creating-and-submitting',
        title: 'Creating and submitting content',
        faqs: [
            {
                question: 'How closely do I need to follow the brief?',
                answer: [
                    'Some briefs are very specific. Others give you much more creative freedom.',
                    'You should always create in your usual style, but make sure you follow any requirements clearly stated in the brief.',
                    "Posts that clearly don't follow the brief or are very low quality can be rejected.",
                ],
            },
            {
                question: 'Do I need to use the exact sound link?',
                answer: [
                    'Usually, yes.',
                    "Wave checks the sound when you submit your post. If you've used the wrong sound, you won't be able to complete the submission.",
                    'Always use the exact link provided in the campaign.',
                ],
            },
            {
                question: 'Can I use any language in my post?',
                answer: [
                    'The required language will be stated in the campaign brief.',
                    'Wave runs campaigns across different countries and languages, so posting in your native language can absolutely be an advantage.',
                ],
            },
            {
                question: 'Do I need to use #ad or a paid partnership label?',
                answer: ['Only if the campaign brief specifically asks you to.'],
            },
            {
                question: 'When does my post need to be submitted?',
                answer: [
                    'Your post must be submitted through Wave before the campaign closes.',
                    "Publishing it on TikTok, Instagram or YouTube before the deadline isn't enough.",
                    'If you post on time but forget to submit the link into Wave until after the campaign closes, the post will not be eligible for payment.',
                ],
            },
            {
                question: 'What happens after I submit?',
                answer: [
                    'Your post moves to In Review.',
                    'A member of the Wave team checks that it meets the campaign requirements. Reviews currently happen once per working day, so there can be additional delays over weekends.',
                    'If approved, the post becomes eligible for payment.',
                    "If rejected, you'll be given a specific reason explaining what needs to change.",
                ],
            },
            {
                question: 'Will I get an email when my post is approved or rejected?',
                answer: [
                    'No.',
                    'You should check the campaign invite page in Wave to see the status of your submission.',
                ],
            },
            {
                question: 'Can I fix and resubmit a rejected post?',
                answer: ['Yes.', 'If the campaign is still accepting submissions, you can fix the issue and resubmit.'],
            },
            {
                question: 'What if my post is still In Review when the campaign closes?',
                answer: [
                    "That's okay as long as you submitted it before the deadline.",
                    'It can still be approved afterwards. On CPM campaigns, it can continue earning if campaign budget remains.',
                ],
            },
            {
                question: "Can I edit my post after it's approved?",
                answer: ['Leave the post exactly as approved unless Wave specifically asks you to change something.'],
            },
        ],
    },
    {
        id: 'views-and-earnings',
        title: 'Views and earnings',
        faqs: [
            {
                question: 'When do views update?',
                answer: [
                    'Views update roughly every 8 hours.',
                    'Earnings follow a few hours later, so allow up to 12 hours for a new view count to be reflected in your earnings.',
                ],
            },
            {
                question: 'Do views earned before my post is approved count?',
                answer: [
                    'Yes.',
                    'Once your post is approved, eligible views gained from the time the post was originally published can count toward your earnings.',
                ],
            },
            {
                question: 'How long can a CPM post keep earning?',
                answer: [
                    'A CPM post can continue earning until it hits its post maximum, your creator maximum, the overall campaign budget is exhausted, or the earning window ends.',
                    'If budget remains, posts can continue accruing value for up to 5 days after the campaign closes.',
                ],
            },
            {
                question: 'What does Pending mean?',
                answer: [
                    "Pending means your earnings have been recorded but haven't fully settled yet.",
                    'This can happen while a CPM post is still earning or while a campaign is finishing.',
                    "You don't need to do anything — Pending earnings move to Available automatically when they're ready.",
                ],
            },
            {
                question: 'What does Available mean?',
                answer: ['Available earnings are ready to withdraw from your Wave wallet.'],
            },
        ],
    },
    {
        id: 'getting-paid',
        title: 'Getting paid',
        faqs: [
            {
                question: 'How much do I need before I can withdraw?',
                answer: [
                    'The minimum withdrawal is $20.',
                    'If your balance is below $20, it stays in your wallet until future earnings take you above the threshold.',
                    'Your balance does not expire.',
                ],
            },
            {
                question: 'How do I withdraw my money?',
                answer: [
                    'Wave currently pays through PayPal.',
                    "When withdrawing, you'll enter the PayPal email address you want us to send the money to.",
                ],
            },
            {
                question: 'How long does a withdrawal take?',
                answer: [
                    'Usually 2–3 working days.',
                    "Wave doesn't process withdrawals over weekends, so withdrawals made on Saturday or Sunday will begin processing on Monday.",
                ],
            },
            {
                question: 'Are there any withdrawal fees?',
                answer: [
                    'Wave does not charge a PayPal or flat withdrawal fee.',
                    'However, if a campaign pays in a different currency from your payout currency, Wave applies a conversion buffer of approximately 4.5% to cover currency conversion.',
                ],
            },
            {
                question: 'Do I need to send an invoice?',
                answer: ['No. Payments are handled through your Wave wallet.'],
            },
            {
                question: 'Does Wave handle my taxes?',
                answer: [
                    'No.',
                    'Creators are responsible for dealing with any tax obligations relating to their Wave earnings.',
                ],
            },
            {
                question: "My withdrawal says Paid but I haven't received it. What should I do?",
                answer: [
                    `Email ${SUPPORT_EMAIL} and include your name, account handle, amount withdrawn, withdrawal date, PayPal email and Wave payment reference — for example WV-98e75cc7.`,
                    'Screenshots of the payment page are also very helpful.',
                ],
            },
        ],
    },
    {
        id: 'your-account',
        title: 'Your account',
        faqs: [
            {
                question: 'Can I have more than one social page on Wave?',
                answer: [
                    'Yes.',
                    'A creator can have multiple TikTok, Instagram and YouTube pages associated with their Wave account.',
                ],
            },
            {
                question: 'How do I add another page after signing up?',
                answer: [`Email ${SUPPORT_EMAIL} and we'll help add it.`],
            },
            {
                question: 'What does “account not linked” mean?',
                answer: [
                    "It means the social page you're trying to submit from hasn't been added to your Wave account.",
                    `Contact ${SUPPORT_EMAIL} if you need the page added.`,
                ],
            },
            {
                question: 'How do I know which page has been invited?',
                answer: [
                    'Check the campaign invite page.',
                    'Campaign participation and submission limits are currently applied per invite.',
                ],
            },
            {
                question: 'What if I change my social username?',
                answer: [
                    "Wave doesn't currently update handle changes automatically.",
                    `Email ${SUPPORT_EMAIL} with your current handle and your new handle.`,
                ],
            },
            {
                question: 'Can I change my rates, location or content categories?',
                answer: [
                    "These can't currently be edited directly in your Wave account.",
                    `Email ${SUPPORT_EMAIL} if you need something updated.`,
                ],
            },
            {
                question: 'How do I log in?',
                answer: [
                    'Go to: app.wave.me/login',
                    "Enter the email address you signed up with and we'll send you a one-time login code.",
                    "If your code doesn't arrive, check your spam/junk folder, make sure you're using the correct email address, request another code and then contact us if you're still having trouble.",
                ],
            },
        ],
    },
    {
        id: 'deadlines-and-time-zones',
        title: 'Deadlines and time zones',
        faqs: [
            {
                question: 'What timezone are campaign deadlines shown in?',
                answer: [
                    'Inside the Wave app, deadlines are shown in your local timezone.',
                    'Campaign invitation emails currently show deadlines in UK time.',
                    "If you're unsure, use the time shown inside Wave as your reference.",
                ],
            },
        ],
    },
    {
        id: 'keeping-posts-live',
        title: 'Keeping posts live',
        faqs: [
            {
                question: 'How long do I need to keep my post live?',
                answer: [
                    'Approved posts should ideally stay live indefinitely — the client has commissioned that placement.',
                    'The absolute minimum is 45 days.',
                    'Deleting or archiving an approved post before 45 days can result in removal from Wave.',
                ],
            },
            {
                question: 'Does Wave own my content?',
                answer: [
                    'No.',
                    'You own your content.',
                    "Wave or the client does not automatically receive additional rights to reuse your post elsewhere. If we want to use your content in another way, or request a Spark/boost code, we'll ask for your permission first.",
                ],
            },
        ],
    },
    {
        id: 'safety-and-fair-use',
        title: 'Safety and fair use',
        faqs: [
            {
                question: 'Can I buy or artificially boost views?',
                answer: [
                    'No.',
                    'Wave has a zero-tolerance policy on fake or botted views.',
                    'Evidence of fraudulent activity can result in removal from Wave and the account being reported to the relevant platform.',
                    'Creators can also be removed for fake accounts, offensive or inappropriate content, repeated campaign violations or abusive behaviour.',
                ],
            },
        ],
    },
    {
        id: 'getting-help',
        title: 'Getting help',
        faqs: [
            {
                question: 'I have a question about a specific campaign. Who should I contact?',
                answer: ['Reply directly to the original campaign invitation email.'],
            },
            {
                question: 'I have an account, page, login or payment problem. Who should I contact?',
                answer: [
                    `Email ${SUPPORT_EMAIL}.`,
                    'Please include your name and account handle, campaign name, post URL and relevant screenshots where applicable.',
                    'We currently aim to reply within 3 working days.',
                ],
            },
            {
                question: 'How do I delete my Wave account or request deletion of my data?',
                answer: [`Email ${SUPPORT_EMAIL} and the team will help you.`],
            },
        ],
    },
];
